import { Box, MenuItem, Stack, TextField } from '@mui/material';
import { DocumentStatus } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PaloStatusPill, PaloStatusStep, paloStatusStepIsFixed } from '@synergy/components-palo';
import { PoolButton, PoolTypography } from '@synergy/components';
import { getDocumentStatusTranslationId } from '@synergy/utils';
import {
    getInternalSettings,
    internalSettingsSelector,
    saveInternalSettings,
    useAppDispatch,
} from '@synergy/admin/app-settings/data-access';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

/**
 * The scale rides the boot call, which the admin fetches once at start-up.
 * A change therefore reaches a session on its next full reload, including the
 * session that made it — the hint below says so, because silently doing
 * nothing is exactly how a working setting gets reported as broken.
 */

/**
 * The five steps, in the order the customer reads them. The value is what goes
 * into the column; the label is what the mask shows.
 */
const steps: { value: PaloStatusStep; label: string }[] = [
    { value: PaloStatusStep.Draft, label: 'Entwurf' },
    { value: PaloStatusStep.InProgress, label: 'In Arbeit' },
    { value: PaloStatusStep.WaitingForMe, label: 'Wartet auf mich' },
    { value: PaloStatusStep.Problem, label: 'Problem' },
    { value: PaloStatusStep.Done, label: 'Abgeschlossen' },
];

/**
 * Only the document states are editable here. They are what the customer's
 * per-document-type table is about; the other state enums (import, user,
 * banner) keep their built-in step. The column takes any key, so widening this
 * later needs no schema change.
 *
 * A state whose step is fixed is shown without a choice rather than left out:
 * leaving it out would read as "we forgot it", and offering a choice that the
 * pill then ignores is worse than offering none (#2216, R-106).
 */
const editableStates = Object.values(DocumentStatus);

export const StatusScale = () => {
    const dispatch = useAppDispatch();
    const { lobsterUrl, defaultCfpTextContract, defaultCfpTextBonus, statusScale, isRequesting } =
        useSelector(internalSettingsSelector);
    const [draft, setDraft] = useState<Record<string, string>>({});

    useEffect(() => {
        dispatch(getInternalSettings());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setDraft(statusScale ?? {});
    }, [statusScale]);

    const handleSave = () => {
        // An empty selection is dropped rather than stored, so the state falls
        // back to its built-in step instead of being pinned to whatever the
        // built-in one happens to be today.
        const cleaned = Object.fromEntries(Object.entries(draft).filter(([, step]) => !!step));

        dispatch(
            saveInternalSettings({
                lobsterXmlOrderUrl: lobsterUrl,
                defaultCfpTextContract,
                defaultCfpTextBonus,
                statusScale: Object.keys(cleaned).length > 0 ? cleaned : null,
            })
        );
    };

    return (
        <Stack gap={1.5} sx={{ width: '100%' }}>
            <PoolTypography
                component="span"
                typography="body1"
                id="app.settings.system.statusScale"
                sx={{ fontWeight: 600 }}
            />

            <PoolTypography component="span" typography="body2" id="app.settings.system.statusScale.hint" />

            <Stack gap={2}>
                {editableStates.map(state => (
                    <Stack key={state} direction="row" gap={2} alignItems="center">
                        <Box sx={{ minWidth: 220 }}>
                            <PaloStatusPill
                                status={state}
                                label={<FormattedMessage id={getDocumentStatusTranslationId(state)} />}
                            />
                        </Box>
                        {paloStatusStepIsFixed(state) ? (
                            <PoolTypography
                                component="span"
                                typography="body2"
                                id="app.settings.system.statusScale.fixed"
                                sx={{ minWidth: 220 }}
                            />
                        ) : (
                            <TextField
                                select={true}
                                size="small"
                                value={draft[state] ?? ''}
                                onChange={event => setDraft({ ...draft, [state]: event.target.value })}
                                sx={{ minWidth: 220 }}
                            >
                                <MenuItem value="">
                                    <FormattedMessage id="app.settings.system.statusScale.builtIn" />
                                </MenuItem>
                                {steps.map(step => (
                                    <MenuItem key={step.value} value={step.value}>
                                        {step.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </Stack>
                ))}

                <Box>
                    <PoolButton
                        styleType="firstLevel"
                        variant="contained"
                        disabled={isRequesting}
                        onClick={handleSave}
                        sx={{ minWidth: 162 }}
                    >
                        <FormattedMessage id="app.common.save" />
                    </PoolButton>
                </Box>
            </Stack>
        </Stack>
    );
};
